import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ImagePlus, X, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function NewComplaintPage() {
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [form, setForm] = useState({
    number: "",
    driver_name: "",
    complaint_date: new Date().toISOString().slice(0, 10),
    deadline: "",
    notes: "",
  });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [dragging, setDragging] = useState(false);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const addFiles = (selected) => {
    const list = Array.from(selected).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setFiles((prev) => [...prev, ...list]);
    setPreviews((prev) => [
      ...prev,
      ...list.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })),
    ]);
  };

  const removeFile = (i) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
    setPreviews((prev) => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[i].url);
      copy.splice(i, 1);
      return copy;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.number || !form.driver_name || !form.complaint_date || !form.deadline) {
      setErr("Completa los campos obligatorios.");
      return;
    }
    if (form.deadline < form.complaint_date) {
      setErr("La fecha límite no puede ser anterior a la fecha de la reclamación.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/complaints", form);
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        await api.post(`/complaints/${data.id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Reclamación creada");
      navigate(`/complaints/${data.id}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-5xl mx-auto" data-testid="new-complaint-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-2 mb-6"
        data-testid="back-btn"
      >
        <ArrowLeft size={14} /> Volver
      </button>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
          Registro
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
          Nueva reclamación
        </h1>
      </div>

      <form onSubmit={submit} className="border border-black bg-white" data-testid="new-complaint-form">
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-black">
          <div>
            <Label className="text-xs uppercase tracking-widest">Número de reclamación *</Label>
            <Input
              value={form.number}
              onChange={onChange("number")}
              placeholder="Ej. RCL-2026-001"
              className="mt-2 h-11 rounded-none border-black"
              required
              data-testid="input-number"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Conductor reclamado *</Label>
            <Input
              value={form.driver_name}
              onChange={onChange("driver_name")}
              placeholder="Nombre completo"
              className="mt-2 h-11 rounded-none border-black"
              required
              data-testid="input-driver"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Fecha de la reclamación *</Label>
            <Input
              type="date"
              value={form.complaint_date}
              onChange={onChange("complaint_date")}
              className="mt-2 h-11 rounded-none border-black"
              required
              data-testid="input-complaint-date"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#E63946]">
              Fecha límite para resolver *
            </Label>
            <Input
              type="date"
              value={form.deadline}
              onChange={onChange("deadline")}
              className="mt-2 h-11 rounded-none border-[#E63946]"
              required
              data-testid="input-deadline"
            />
            <p className="text-[11px] text-zinc-500 mt-2">
              Si esta fecha se sobrepasa y la reclamación no está resuelta/multada, se marcará en rojo.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Notas iniciales</Label>
            <Textarea
              value={form.notes}
              onChange={onChange("notes")}
              placeholder="Describe la reclamación, contexto, y observaciones iniciales…"
              className="mt-2 rounded-none border-black min-h-[120px]"
              data-testid="input-notes"
            />
          </div>
        </div>

        {/* Evidencias */}
        <div className="p-6">
          <Label className="text-xs uppercase tracking-widest">Evidencias (fotos)</Label>
          <div
            className={`mt-3 dropzone p-8 text-center cursor-pointer ${
              dragging ? "dragging" : ""
            }`}
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              addFiles(e.dataTransfer.files);
            }}
            data-testid="upload-dropzone"
          >
            <ImagePlus className="mx-auto mb-3" />
            <div className="font-display text-lg font-semibold">
              Arrastra fotos aquí o haz clic para seleccionar
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              Puedes subir muchas fotos a la vez (JPG, PNG, WEBP, HEIC).
            </div>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => addFiles(e.target.files)}
              data-testid="upload-evidence-input"
            />
          </div>

          {previews.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4" data-testid="upload-previews">
              {previews.map((p, i) => (
                <div key={i} className="relative border border-black aspect-square overflow-hidden">
                  <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="absolute top-1 right-1 bg-black text-white p-1 hover:bg-zinc-700"
                    data-testid={`remove-preview-${i}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {err && (
          <div
            className="mx-6 mb-4 text-sm text-red-700 bg-red-50 border border-red-300 px-3 py-2"
            data-testid="form-error"
          >
            {err}
          </div>
        )}

        <div className="border-t border-black p-4 flex justify-end gap-3 bg-zinc-50">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            className="rounded-none border-black h-11 px-5 text-xs uppercase tracking-widest"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="rounded-none btn-primary h-11 px-6 text-xs uppercase tracking-widest"
            data-testid="submit-complaint-btn"
          >
            {submitting ? <Loader2 className="animate-spin" size={16} /> : "Registrar reclamación"}
          </Button>
        </div>
      </form>
    </div>
  );
}
